import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { verifyUser, resendVerification } from '../api';

const VerifyUser = () => {
  console.log('VerifyUser component rendered');
  const location = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(''); // State for error message
  const [showResendForm, setShowResendForm] = useState(false); // State for resend form
  const [email, setEmail] = useState(''); // State for email input
  const [isResending, setIsResending] = useState(false); // State for resend loading
  const [resendMessage, setResendMessage] = useState(''); // State for resend success/error message

  useEffect(() => {
    console.log('Verifying user...');
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const uuid = queryParams.get('uuid');

    console.log('token:', token);
    console.log('uuid:', uuid);

    const verify = async () => {
      try {
        const response = await verifyUser({ token, uuid });
        // Redirect to login page with success message
        navigate('/login', { state: { message: response.message } });
      } catch (error) {
        // Handle specific error cases
        console.error('Verification failed:', error);
        
        if (error.type === 'TOKEN_EXPIRED') {
          setErrorMessage('Your verification link has expired. Please request a new verification email.');
          setShowResendForm(true);
        } else if (error.type === 'EMAIL_ALREADY_VERIFIED') {
          setErrorMessage('Your email has already been verified. Please login to continue.');
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setErrorMessage('Verification failed. Please try again or request a new verification email.');
          setShowResendForm(true);
        }
      }
    };

    if (token && uuid) {
      verify();
    } else {
      // Handle missing parameters
      console.error('Missing token or uuid');
      setErrorMessage('Missing token or uuid. Please check your verification link or request a new one.');
      setShowResendForm(true);
    }
  }, [location, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setResendMessage('');
    
    if (!email.trim()) {
      setResendMessage('Please enter your email address.');
      return;
    }

    if (!validateEmail(email)) {
      setResendMessage('Please enter a valid email address.');
      return;
    }

    setIsResending(true);

    try {
      await resendVerification({ email: email.trim() });
      setResendMessage('Verification email sent successfully! Please check your inbox for an email from no-reply@courtbookings.co.za. If you do not see it, please check your spam/junk folder.');
      setErrorMessage(''); // Clear the original error message
      setShowResendForm(false);
    } catch (error) {
      console.error('Resend verification failed:', error);
      if (error.type === 'USER_NOT_FOUND') {
        setResendMessage('No account found with this email address. Please check your email or register.');
      } else if (error.type === 'EMAIL_ALREADY_VERIFIED') {
        setResendMessage('This email has already been verified. Please login to continue.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setResendMessage('Failed to send verification email. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm">
        <div className="relative flex items-center mb-6">
          <h2 className="w-full text-center text-2xl font-bold text-slate-800">Email Verification</h2>
        </div>
        
        {/* Display error message */}
        {errorMessage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
            {errorMessage}
          </div>
        )}

        {/* Display resend success/error message */}
        {resendMessage && (
          <div className={`mb-4 p-3 border rounded-md text-sm ${
            resendMessage.includes('successfully') 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {resendMessage}
          </div>
        )}

        {/* Resend verification form */}
        {showResendForm && (
          <form onSubmit={handleResendVerification} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-600 mb-1">
                Email Address
              </label>
                             <input
                 id="email"
                 name="email"
                 type="text"
                 autoComplete="email"
                 value={email}
                 onChange={(e) => setEmail(e.target.value)}
                 className="w-full px-4 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                 placeholder="you@example.com"
                 disabled={isResending}
               />
            </div>
            <button
              type="submit"
              disabled={isResending}
              className={`w-full py-2 px-4 rounded-md text-white font-medium transition-all duration-200 ${
                isResending 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
              } flex items-center justify-center`}
            >
              {isResending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                'Send New Verification Email'
              )}
            </button>
          </form>
        )}
        
        <p className="mt-4 text-center text-sm text-slate-600">
          <button
            onClick={() => navigate('/login')}
            className="font-medium text-blue-600 hover:text-blue-500 transition"
          >
            Back to Login
          </button>
        </p>
      </div>
    </div>
  );
};

export default VerifyUser;
